import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { Button, ButtonGroup, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';

import {
  applyOrToggleStyle,
  getSelectionStyles,
  DEFAULT_STYLES,
  getEditorElement,
} from '@common/helpers/domManipulationHelpers';

function getRangeIfFullyInside(rootEl) {
  const sel = window.getSelection?.();
  if (!sel || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  const { startContainer, endContainer } = range;

  const insideStart = rootEl.contains(startContainer);
  const insideEnd = rootEl.contains(endContainer);

  return insideStart && insideEnd ? range : null;
}

const TextDecoration = ({ options = [] }) => {
  const { t } = useTranslation();

  const [activeStyles, setActiveStyles] = useState(DEFAULT_STYLES);

  useEffect(() => {
    const myDocument = getEditorElement();
    const updateStyles = () => setActiveStyles(getSelectionStyles());

    updateStyles();

    document.addEventListener('selectionchange', () => {
      const range = getRangeIfFullyInside(myDocument);
      if (!range) {
        return;
      }
      updateStyles();
    });
    return () => document.removeEventListener('selectionchange', updateStyles);
  }, []);

  const onClick = (event) => {
    const fieldName = event.currentTarget.name;

    const isActive = activeStyles[fieldName];

    applyOrToggleStyle(fieldName, isActive);

    requestAnimationFrame(() => {
      setActiveStyles(getSelectionStyles());
    });
  };

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'bold':
        return <FormatBoldIcon />;
      case 'underline':
        return <FormatUnderlinedIcon />;
      case 'italic':
        return <FormatItalicIcon />;
      default:
        return null;
    }
  };

  return (
    <div style={{ display: 'block' }}>
      <ButtonGroup
        disableElevation
        sx={{ border: 'none' }}
        aria-label='Basic button group'
        size='small'
      >
        {options.map((option) => (
          <Tooltip key={option.name} title={t(option.label)}>
            <Button
              name={option.name}
              onMouseDown={(e) => e.preventDefault()}
              onClick={onClick}
              sx={(theme) => ({
                p: 1,
                '& span': {
                  m: 0,
                },
                backgroundColor: theme.palette.secondary.light,
                color: activeStyles[option.name]
                  ? theme.palette.secondary.dark
                  : theme.common.buttonInactive,
                border: `none`,
              })}
              startIcon={renderIcon(option.iconName)}
            />
          </Tooltip>
        ))}
      </ButtonGroup>
    </div>
  );
};

export default TextDecoration;
